import { z } from 'zod';

export const shippingPartySchema = z.object({
    duns: z.string(),
    supplier_code: z.string().nullish(),
});

const transportLegPartyAddressSchema = z.object({
    street: z.string(),
    city: z.string(),
    postal_code: z.string(),
    country_code: z.string(),
});

const handlingLocationSchema = z.object({
    id: z.string(),
    duns: z.string().nullish(),
    supplier_code: z.string().nullish(),
    address: transportLegPartyAddressSchema,
});

const supplierSchema = z.object({
    type: z.literal('SUPPLIER'),
    duns: z.string(),
    supplier_code: z.string(),
    name: z.string(),
    address: transportLegPartyAddressSchema,
    handling_location: handlingLocationSchema.nullish(),
});

const crossDockSchema = z.object({
    type: z.literal('CROSS_DOCK'),
    duns: z.string(),
    name: z.string(),
    address: transportLegPartyAddressSchema,
});

const plantSchema = z.object({
    type: z.literal('PLANT'),
    duns: z.string(),
    supplier_code: z.string().nullish(),
    name: z.string(),
    address: transportLegPartyAddressSchema,
    plant_code: z.string(),
    handling_location: handlingLocationSchema.nullish(),
});

const transportLegPartySchema = z.discriminatedUnion('type', [supplierSchema, crossDockSchema, plantSchema]);

const placeOfDischargeSchema = z.object({ id: z.string() });

const placeOfLoadingSchema = z.object({ id: z.string() });

const loadingLocationSchema = z.object({
    ship_from: z.object({ id: z.string() }),
    place_of_loading: placeOfLoadingSchema.nullish(),
});

const unloadingLocationSchema = z.object({
    ship_to: z.object({ id: z.string() }),
    place_of_discharge: placeOfDischargeSchema.nullish(),
});

const freightForwarderSchema = z.object({
    duns: z.string(),
    supplier_code: z.string().nullish(),
    name: z.string().nullish(),
    address: transportLegPartyAddressSchema.nullish(),
});

const transportLegSchema = z.object({
    transport_leg_id: z.string(),
    transport_leg_number: z.number(),
    loading: z.object({
        type: z.enum(['CONSIGNOR', 'CROSS_DOCK']),
        party: transportLegPartySchema,
    }),
    unloading: z.object({
        type: z.enum(['CROSS_DOCK', 'CONSIGNEE']),
        party: transportLegPartySchema,
    }),
    freight_forwarder: freightForwarderSchema,
    delivery_date: z.iso.datetime(),
    pickup_date: z.iso.datetime(),
});

const regularRouteSchema = z.object({
    type: z.literal('REGULAR'),
    route_id: z.string(),
    transport_legs: z.array(transportLegSchema),
});

const fallbackRouteSchema = z.object({
    type: z.literal('FALLBACK'),
    consignor: shippingPartySchema,
    consignee: shippingPartySchema,
    freight_forwarder: freightForwarderSchema,
});

const routeSchema = z.discriminatedUnion('type', [regularRouteSchema, fallbackRouteSchema]);

export const statusSchema = z.enum([
    'DESPATCH_COMPLETED',
    'PICKUP_COMPLETED',
    'CROSS_DOCK_DROPOFF_COMPLETED',
    'CROSS_DOCK_RECEIPT_CONFIRMED',
    'TRANSPORT_NOTIFIED',
    'TRANSPORT_CONFIRMED',
    'CROSS_DOCK_ISSUE_CONFIRMED',
    'CROSS_DOCK_PICKUP_COMPLETED',
    'DELIVERY_CONFIRMED',
    'DELIVERY_COMPLETED',
]);

const physicalLocationSchema = z.object({
    name: z.string(),
    city: z.string(),
    country_code: z.string(),
});

const statusLocationSchema = z.union([
    z.object({
        physical_location: physicalLocationSchema,
        duns: z.never().optional(),
    }),
    z.object({
        physical_location: z.never().optional(),
        duns: z.string(),
    }),
]);

const timeWindowSchema = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
});

const firstTransportOrderSchema = z.object({
    transport_order_number: z.string(),
    requested_pickup_at: timeWindowSchema,
    external_bundle_id: z.string().nullish(),
    access_code: z.string().nullish(),
});

const shipmentSchema = z.object({
    id: z.string(),
    shipment_number: z.string(),
    packaging_id: z.string(),
    status: statusSchema,
    status_location: statusLocationSchema,
    has_deviations: z.boolean(),
    consignor: shippingPartySchema,
    consignee: shippingPartySchema,
    loading_location: loadingLocationSchema,
    unloading_location: unloadingLocationSchema,
    route: routeSchema,
    calculated_delivery_time_at: z.iso.datetime().nullish(),
    pickup_confirmed_at: z.iso.datetime(),
    first_transport_order: firstTransportOrderSchema.nullish(),
});

export const shipmentsQueryDateTypeSchema = z.enum([
    'CALCULATED_DELIVERY_DATE',
    'PICKUP_CONFIRMATION_DATE',
    'REQUESTED_PICKUP_DATE',
]);

export const getShipmentsResponseSchema = z.object({
    items: z.array(shipmentSchema),
    has_more: z.boolean(),
});

export type ApiGetShipmentsResponse = z.infer<typeof getShipmentsResponseSchema>;
export type ApiShipment = z.infer<typeof shipmentSchema>;
export type ApiStatusLocation = z.infer<typeof statusLocationSchema>;
export type ApiStatus = z.infer<typeof statusSchema>;
export type ApiFirstTransportOrder = z.infer<typeof firstTransportOrderSchema>;
export type ApiShippingParty = z.infer<typeof shippingPartySchema>;
export type ApiShippingPartyAddress = z.infer<typeof transportLegPartyAddressSchema>;
export type ApiTransportLegParty = z.infer<typeof transportLegPartySchema>;
export type ApiSupplierParty = z.infer<typeof supplierSchema>;
export type ApiPlantParty = z.infer<typeof plantSchema>;
export type ApiCrossDockParty = z.infer<typeof crossDockSchema>;
export type ApiHandlingLocation = z.infer<typeof handlingLocationSchema>;
export type ApiLoadingLocation = z.infer<typeof loadingLocationSchema>;
export type ApiPlaceOfLoading = z.infer<typeof placeOfLoadingSchema>;
export type ApiUnloadingLocation = z.infer<typeof unloadingLocationSchema>;
export type ApiPlaceOfDischarge = z.infer<typeof placeOfDischargeSchema>;
export type ApiRegularRoute = z.infer<typeof regularRouteSchema>;
export type ApiTransportLeg = z.infer<typeof transportLegSchema>;
export type ApiFallbackRoute = z.infer<typeof fallbackRouteSchema>;
export type ApiRoute = z.infer<typeof routeSchema>;
export type ApiFreightForwarder = z.infer<typeof freightForwarderSchema>;
export type ApiTimeWindow = z.infer<typeof timeWindowSchema>;
export type ApiShipmentDateTypeQuery = z.infer<typeof shipmentsQueryDateTypeSchema>;
